// The in-memory API's access domain (coverage matrix §2.1, §2.2, §2.9): invitations and their
// acceptance, people edits, roles, the caller's own membership, magic links, sessions, password
// and profile. Kept apart from fake.ts so the four UI branches touch fake.ts in a few lines only.
//
// It follows the real routes' rules (services/api/src/routes/org/people.ts, invitations.ts):
// - invite: `owner` only by an owner, `admin` by an owner or admin, `member` (the default,
//   MEMBER-ROLE) and `client_viewer` by any manager; an active member's email is 409; a pending
//   invitation of the same email is
//   superseded (its token stops working); the invitee gets a mail with
//   `/invitations/accept?token=`;
// - accept: a signed-in session whose email equals the invitation's (a magic link proves it);
//   otherwise 404 `invitation_invalid`, as the real route;
// - PATCH person: an owner membership only by an owner, an admin one by an owner or admin, an
//   agency operator never touches owner/admin and changes its own membership only between
//   member and client_viewer; `roles` sets the managed roles (owner, admin, member,
//   client_viewer; owner granted by an owner only); deactivation drops every role; the last
//   active owner cannot leave `active` or lose `owner` (409).
// Mail never leaves the process: `mailbox` holds what would have been sent.
import type { OperationOutput, Problem } from "@companyos/sdk";
import { uid } from "./fake-data";
import type { CompanyRole, MeResponse, Person } from "./types";

type Invitation = OperationOutput<"people.invite">;
type Role = OperationOutput<"roles.list">["data"][number];
type CompanyLike = {
  id: string;
  slug: string;
  name: string;
  calendar_config: { timezone: string; weekStartDay: number; fiscalYearStartMonth: number };
};

export type FakeMail = { to: string; subject: string; link: string; at: string };
export type FakeSession = {
  id: string;
  email: string;
  createdAt: string;
  expiresAt: string;
  userAgent: string | null;
};
type Account = { email: string; password: string; label: string; me: MeResponse };

type Principal = {
  me: MeResponse;
  company: CompanyLike;
  roles: CompanyRole[];
  operator: boolean;
  manager: boolean;
};
type Ctx = {
  url: URL;
  params: Record<string, string>;
  body: unknown;
  me: MeResponse;
  principal: Principal | null;
};
type Handler = (ctx: Ctx) => Response;

export type AccessDeps = {
  accounts: Map<string, Account>;
  companies: CompanyLike[];
  now: () => Date;
  current: () => string | null;
  setCurrent: (email: string | null) => void;
  /** The generated people of a company (fake-entry's `people`), before edits. */
  generatedPeople: (p: Principal) => Person[];
  entitlementsOf: (companyId: string) => MeResponse["entitlements"][string];
  /** Metric keys a role may name (the company's definitions, which include the DE catalog). */
  metricKeys: (companyId: string) => string[];
  problem: (
    status: number,
    code: Problem["code"],
    title: string,
    detail?: string,
    extras?: Record<string, unknown>,
  ) => Response;
  json: (body: unknown, status?: number) => Response;
  page: <T>(items: T[], url: URL) => unknown;
  /** HARDEN-mfa: the account uses two-step sign-in (no sign-in link is mailed or honoured). */
  twoFactorEnabled: (email: string) => boolean;
  /** ACTIONS: how many seats (open or closed) name the role (`roles.delete` refuses then). */
  roleSeats?: (companyId: string, roleId: string) => number;
};

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const MANAGED = ["owner", "admin", "member", "client_viewer"] as const;
/** Roles an agency operator may set on its own membership (no authority; MEMBER-ROLE). */
const SELF_SERVE = ["member", "client_viewer"];
const DAY = 86_400_000;
const companyNo = (companyId: string) => Number.parseInt(companyId.slice(-4), 16) % 0x0fff;

/** A token shaped like the real one: `{company id}.{invitation id}.{43 base64url chars}`. */
function newToken(companyId: string, invitationId: string, n: number): string {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
  let secret = "";
  let x = (n * 2654435761 + Date.now()) >>> 0;
  for (let i = 0; i < 43; i++) {
    x = (x * 1103515245 + 12345) >>> 0;
    secret += alphabet[(x >>> 8) % 64];
  }
  return `${companyId}.${invitationId}.${secret}`;
}

export function createAccessFake(d: AccessDeps) {
  const invitations: (Invitation & { token: string })[] = [];
  const mailbox: FakeMail[] = [];
  const magic = new Map<string, { email: string; name?: string; callbackPath: string }>();
  const passwordless = new Set<string>();
  const resets = new Map<string, string>();
  const sessions: FakeSession[] = [];
  let currentSession: string | null = null;
  /** People who accepted an invitation, per company, and edits on any person. */
  const joined = new Map<string, Person[]>();
  const edits = new Map<string, Partial<Person>>();
  const roleStore = new Map<string, Role[]>();
  let seq = 0x100;

  const at = () => d.now().toISOString();

  function mail(to: string, subject: string, link: string) {
    mailbox.push({ to, subject, link, at: at() });
  }

  function peopleOf(p: Principal): Person[] {
    const list = [...d.generatedPeople(p), ...(joined.get(p.company.id) ?? [])];
    return list.map((x) => ({ ...x, ...edits.get(x.id) }));
  }

  function rolesOf(companyId: string): Role[] {
    let list = roleStore.get(companyId);
    if (!list) {
      list = [
        {
          company_id: companyId,
          id: uid(0x2f00, companyNo(companyId)),
          name: "Recruiter",
          description: "Sources and screens candidates.",
          default_metric_keys: ["de.candidates_sourced", "de.interviews_booked"],
          actor_kind: "system",
          actor_membership_id: null,
          created_at: "2026-06-01T09:00:00Z",
          updated_at: "2026-06-01T09:00:00Z",
        },
      ];
      roleStore.set(companyId, list);
    }
    return list;
  }

  /** The real route's check (routes/org/roles.ts): known keys, each once. */
  function badMetricKeys(companyId: string, keys: unknown): string | null {
    if (keys === undefined) return null;
    if (!Array.isArray(keys)) return "default_metric_keys: a list of keys";
    if (new Set(keys).size !== keys.length) return "default_metric_keys: a key may appear once";
    const known = new Set(d.metricKeys(companyId));
    const missing = keys.filter((k) => !known.has(k as string));
    return missing.length
      ? `default_metric_keys: unknown metric keys: ${missing.join(", ")}`
      : null;
  }

  const forbidden = (detail: string) => d.problem(403, "forbidden", "Forbidden", detail);
  const validation = (detail: string) =>
    d.problem(422, "validation_failed", "Validation failed", detail);

  /** Creates (or supersedes) the pending invitation of `email` and mails its link. */
  function invite(
    company: CompanyLike,
    input: { email: string; display_name: string; roles: Invitation["roles"] },
    actor: Invitation["actor_kind"],
  ): Invitation {
    const email = input.email.trim().toLowerCase();
    for (const i of invitations)
      if (i.company_id === company.id && i.email === email && i.status === "pending") {
        i.status = "revoked";
        i.updated_at = at();
      }
    const id = uid(0x1e00, ++seq);
    const token = newToken(company.id, id, seq);
    const row = {
      company_id: company.id,
      id,
      email,
      display_name: input.display_name.trim(),
      roles: [...new Set(input.roles)].sort() as Invitation["roles"],
      status: "pending" as const,
      version: 1,
      sent_at: at(),
      expires_at: new Date(d.now().getTime() + 14 * DAY).toISOString(),
      membership_id: null,
      accepted_at: null,
      actor_kind: actor,
      actor_membership_id: null,
      created_at: at(),
      updated_at: at(),
      token,
    };
    invitations.push(row);
    mail(email, "You have been invited to CompanyOS", `/invitations/accept?token=${token}`);
    const { token: _t, ...out } = row;
    return out;
  }

  /** The company-wide authority of the caller (owner / admin / operator only). */
  const authority = (p: Principal) => ({
    owner: p.roles.includes("owner"),
    admin: p.roles.includes("admin"),
    operatorOnly: p.operator && !p.roles.includes("owner") && !p.roles.includes("admin"),
  });

  const handlers: Partial<Record<string, Handler>> = {
    "people.invite": ({ principal: p, body }) => {
      if (!p) return d.problem(404, "not_found", "Not found");
      if (!p.manager) return forbidden("managers only");
      const b = (body ?? {}) as { email?: unknown; display_name?: unknown; roles?: unknown };
      if (typeof b.email !== "string" || !EMAIL.test(b.email.trim()))
        return validation("email: not an email address");
      if (typeof b.display_name !== "string" || !b.display_name.trim())
        return validation("display_name: required");
      // MEMBER-ROLE: `member` is the default role of a new invite.
      const roles =
        b.roles === undefined ? ["member"] : Array.isArray(b.roles) ? (b.roles as string[]) : [];
      if (
        !roles.length ||
        roles.length > 4 ||
        roles.some((r) => !(MANAGED as readonly string[]).includes(r))
      )
        return validation("roles: one to four of owner, admin, member, client_viewer");
      const a = authority(p);
      if (roles.includes("owner") && !a.owner) return forbidden("only an owner invites an owner");
      if (roles.includes("admin") && !a.owner && !a.admin)
        return forbidden("only an owner or admin invites an admin");
      const email = b.email.trim().toLowerCase();
      if (peopleOf(p).some((x) => x.email === email && x.status === "active"))
        return d.problem(409, "conflict", "Conflict", `${email} is already a member.`);
      return d.json(
        invite(
          p.company,
          {
            email,
            display_name: b.display_name,
            roles: roles as Invitation["roles"],
          },
          p.operator ? "agency_user" : "person",
        ),
        201,
      );
    },

    // Pending invitations of the company: live ones, oldest first, no token (the real route's
    // rules: managers; revoking follows who may invite; a revoked one comes back unchanged).
    "invitations.list": ({ principal: p, url }) => {
      if (!p) return d.problem(404, "not_found", "Not found");
      if (!p.manager) return forbidden("managers only");
      const live = invitations
        .filter(
          (i) =>
            i.company_id === p.company.id &&
            i.status === "pending" &&
            new Date(i.expires_at) > d.now(),
        )
        .map(({ token: _t, ...row }) => row);
      return d.json(d.page(live, url));
    },
    "invitations.revoke": ({ principal: p, params }) => {
      if (!p) return d.problem(404, "not_found", "Not found");
      if (!p.manager) return forbidden("managers only");
      const inv = invitations.find((i) => i.company_id === p.company.id && i.id === params.id);
      if (!inv) return d.problem(404, "not_found", "Not found");
      if (inv.status === "accepted")
        return d.problem(409, "conflict", "Conflict", "This invitation was already accepted.");
      if (inv.status === "pending") {
        const a = authority(p);
        if (inv.roles.includes("owner") && !a.owner)
          return forbidden("only an owner invites an owner");
        if (inv.roles.includes("admin") && !a.owner && !a.admin)
          return forbidden("only an owner or admin invites an admin");
        inv.status = "revoked";
        inv.updated_at = at();
      }
      const { token: _t, ...out } = inv;
      return d.json(out);
    },

    "people.update": ({ principal: p, params, body, me }) => {
      if (!p) return d.problem(404, "not_found", "Not found");
      if (!p.manager) return forbidden("managers only");
      const list = peopleOf(p);
      const target = list.find((x) => x.id === params.id);
      if (!target) return d.problem(404, "not_found", "Not found");
      const b = (body ?? {}) as { display_name?: unknown; status?: unknown; roles?: unknown };
      const a = authority(p);
      const held = target.roles;
      const wanted = Array.isArray(b.roles) ? (b.roles as string[]) : null;
      const grantsAdmin = !!wanted?.includes("admin") && !held.includes("admin");
      const grantsOwner = !!wanted?.includes("owner") && !held.includes("owner");
      const self = target.user_id !== null && target.user_id === me.user.id;
      const plainRolesOnly =
        wanted !== null &&
        b.display_name === undefined &&
        b.status === undefined &&
        [...held, ...wanted].every((r) => SELF_SERVE.includes(r));
      if (a.operatorOnly && self && !plainRolesOnly)
        return forbidden("an agency operator cannot change its own membership of a client");
      if (a.operatorOnly && (held.includes("owner") || held.includes("admin")))
        return forbidden("an agency operator cannot change an owner or admin membership");
      if (held.includes("owner") && !a.owner) return forbidden("only an owner changes an owner");
      if (held.includes("admin") && !a.owner && !a.admin)
        return forbidden("only an owner or admin changes an admin");
      if (grantsAdmin && !a.owner && !a.admin)
        return forbidden("only an owner or admin grants admin");
      if (grantsOwner && !a.owner) return forbidden("only an owner grants owner");
      if (
        wanted !== null &&
        held.includes("owner") &&
        !wanted.includes("owner") &&
        !list.some((x) => x.id !== target.id && x.status === "active" && x.roles.includes("owner"))
      )
        return d.problem(409, "conflict", "Conflict", "The company needs another active owner.");
      const patch: Partial<Person> = {};
      if (b.display_name !== undefined) {
        if (typeof b.display_name !== "string" || !b.display_name.trim())
          return validation("display_name: must not be empty");
        patch.display_name = b.display_name.trim();
      }
      if (b.status !== undefined) {
        if (!["active", "paused", "deactivated"].includes(b.status as string))
          return validation("status: active, paused or deactivated");
        const status = b.status as Person["status"];
        if (
          status !== "active" &&
          held.includes("owner") &&
          !list.some(
            (x) => x.id !== target.id && x.status === "active" && x.roles.includes("owner"),
          )
        )
          return d.problem(409, "conflict", "Conflict", "The company needs another active owner.");
        patch.status = status;
        if (status === "deactivated") patch.roles = [];
      }
      if (b.roles !== undefined) {
        const roles = Array.isArray(b.roles) ? (b.roles as string[]) : null;
        if (!roles || roles.some((r) => !(MANAGED as readonly string[]).includes(r)))
          return validation("roles: owner, admin, member and client_viewer only");
        if ((patch.status ?? target.status) !== "active")
          return validation("roles: only an active person holds roles");
        patch.roles = [
          ...held.filter((r) => !(MANAGED as readonly string[]).includes(r)),
          ...(roles as Person["roles"]),
        ];
      }
      edits.set(target.id, { ...edits.get(target.id), ...patch, updated_at: at() });
      return d.json({ ...target, ...edits.get(target.id) });
    },

    "people.me": ({ principal: p, me }) => {
      if (!p) return d.problem(404, "not_found", "Not found");
      const m = me.memberships.find((x) => x.company_id === p.company.id);
      if (!m) return d.problem(404, "not_found", "Not found");
      const listed = peopleOf(p).find((x) => x.id === m.membership_id);
      if (listed) return d.json(listed);
      return d.json({
        company_id: p.company.id,
        id: m.membership_id,
        user_id: me.user.id,
        kind: "person",
        status: "active",
        display_name: me.user.name || me.user.email,
        avatar_key: null,
        owner_membership_id: null,
        joined_at: "2026-06-01T09:00:00Z",
        actor_kind: "system",
        actor_membership_id: null,
        created_at: "2026-06-01T09:00:00Z",
        updated_at: "2026-06-01T09:00:00Z",
        email: me.user.email,
        roles: m.roles,
      } satisfies Person);
    },

    "roles.list": ({ principal: p, url }) => {
      if (!p) return d.problem(404, "not_found", "Not found");
      // CHART-DIMENSIONS: role names are the org chart's floor; every company viewer reads them.
      return d.json(d.page(rolesOf(p.company.id), url));
    },

    "roles.create": ({ principal: p, body }) => {
      if (!p) return d.problem(404, "not_found", "Not found");
      if (!p.manager) return forbidden("managers only");
      const b = (body ?? {}) as {
        name?: unknown;
        description?: unknown;
        default_metric_keys?: unknown;
      };
      if (typeof b.name !== "string" || !b.name.trim()) return validation("name: required");
      const badKeys = badMetricKeys(p.company.id, b.default_metric_keys);
      if (badKeys) return validation(badKeys);
      const role: Role = {
        company_id: p.company.id,
        id: uid(0x2f10, ++seq),
        name: b.name.trim(),
        description: typeof b.description === "string" ? b.description : null,
        default_metric_keys: Array.isArray(b.default_metric_keys)
          ? (b.default_metric_keys as string[])
          : [],
        actor_kind: p.operator ? "agency_user" : "person",
        actor_membership_id: null,
        created_at: at(),
        updated_at: at(),
      };
      rolesOf(p.company.id).push(role);
      return d.json(role, 201);
    },

    "roles.update": ({ principal: p, params, body }) => {
      if (!p) return d.problem(404, "not_found", "Not found");
      if (!p.manager) return forbidden("managers only");
      const role = rolesOf(p.company.id).find((r) => r.id === params.id);
      if (!role) return d.problem(404, "not_found", "Not found");
      const b = (body ?? {}) as {
        name?: unknown;
        description?: unknown;
        default_metric_keys?: unknown;
      };
      if (b.name !== undefined && (typeof b.name !== "string" || !b.name.trim()))
        return validation("name: must not be empty");
      const badKeys = badMetricKeys(p.company.id, b.default_metric_keys);
      if (badKeys) return validation(badKeys);
      if (typeof b.name === "string") role.name = b.name.trim();
      if (b.description !== undefined)
        role.description = typeof b.description === "string" ? b.description : null;
      if (Array.isArray(b.default_metric_keys))
        role.default_metric_keys = b.default_metric_keys as string[];
      role.updated_at = at();
      return d.json(role);
    },

    // ACTIONS: as the API, only a role no seat names is deleted.
    "roles.delete": ({ principal: p, params }) => {
      if (!p) return d.problem(404, "not_found", "Not found");
      if (!p.manager) return forbidden("managers only");
      const list = rolesOf(p.company.id);
      const i = list.findIndex((r) => r.id === params.id);
      if (i < 0) return d.problem(404, "not_found", "Not found");
      const seats = d.roleSeats?.(p.company.id, params.id as string) ?? 0;
      if (seats > 0)
        return d.problem(
          409,
          "conflict",
          "Conflict",
          `the role is in use (${seats} seat${seats === 1 ? "" : "s"}); move them to another role first`,
        );
      list.splice(i, 1);
      return new Response(null, { status: 204 });
    },

    "invitations.accept": ({ me, body }) => {
      const token = (body as { token?: unknown } | null)?.token;
      // ONBOARD: a matched token's refusal says why (`reason`); an unknown token never does.
      const invalid = (reason?: "expired" | "revoked" | "accepted" | "other_address") =>
        d.problem(
          404,
          "invitation_invalid",
          "No pending invitation for this token and account",
          undefined,
          reason ? { reason } : undefined,
        );
      if (typeof token !== "string") return validation("token: required");
      const inv = invitations.find((i) => i.token === token);
      if (!inv) return invalid();
      if (inv.status === "accepted") return invalid("accepted");
      if (inv.status !== "pending") return invalid("revoked");
      if (new Date(inv.expires_at) <= d.now()) return invalid("expired");
      if (inv.email !== me.user.email.toLowerCase()) return invalid("other_address");
      const company = d.companies.find((c) => c.id === inv.company_id);
      const account = d.accounts.get(me.user.email.toLowerCase());
      if (!company || !account) return invalid();
      const membershipId = uid(0x1c10, ++seq);
      inv.status = "accepted";
      inv.membership_id = membershipId;
      inv.accepted_at = at();
      account.me.memberships.push({
        company_id: company.id,
        company_slug: company.slug,
        company_name: company.name,
        company_calendar: company.calendar_config,
        membership_id: membershipId,
        kind: "person",
        roles: inv.roles,
      });
      account.me.entitlements[company.id] = d.entitlementsOf(company.id);
      const member: Person = {
        company_id: company.id,
        id: membershipId,
        user_id: account.me.user.id,
        kind: "person",
        status: "active",
        display_name: inv.display_name,
        avatar_key: null,
        owner_membership_id: null,
        joined_at: at(),
        actor_kind: "person",
        actor_membership_id: membershipId,
        created_at: at(),
        updated_at: at(),
        email: inv.email,
        roles: inv.roles,
      };
      joined.set(company.id, [...(joined.get(company.id) ?? []), member]);
      const { email: _e, roles: _r, ...membership } = member;
      return d.json({ company_id: company.id, membership, roles: inv.roles });
    },
  };

  function startSession(email: string) {
    const id = uid(0x5e00, ++seq);
    sessions.push({
      id,
      email,
      createdAt: at(),
      expiresAt: new Date(d.now().getTime() + 7 * DAY).toISOString(),
      userAgent: typeof navigator === "undefined" ? null : navigator.userAgent,
    });
    currentSession = id;
  }

  return {
    handlers,
    invite,
    mailbox,
    startSession,
    endSession() {
      currentSession = null;
    },
    sessions(): {
      id: string;
      createdAt: string;
      expiresAt: string;
      userAgent: string | null;
      current: boolean;
    }[] {
      const email = d.current();
      if (email && !sessions.some((s) => s.email === email)) startSession(email);
      return sessions
        .filter((s) => s.email === email)
        .map(({ email: _e, ...s }) => ({ ...s, current: s.id === currentSession }));
    },
    revokeSession(id: string) {
      const i = sessions.findIndex((s) => s.id === id && s.email === d.current());
      if (i < 0) return;
      sessions.splice(i, 1);
      if (id === currentSession) {
        currentSession = null;
        d.setCurrent(null);
      }
    },
    /**
     * Records the link; the demo sign-in buttons open it (`openMagicLink`). HARDEN-mfa: an account
     * with two-step sign-in is mailed nothing (the answer is the same) and gets "" back.
     */
    sendMagicLink(email: string, callbackPath: string, name?: string) {
      const normalized = email.trim().toLowerCase();
      if (d.twoFactorEnabled(normalized)) return "";
      const token = `ml_${(++seq).toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      magic.set(token, { email: normalized, callbackPath, ...(name ? { name } : {}) });
      mail(normalized, "Your CompanyOS sign-in link", `/v1/auth/magic-link/verify?token=${token}`);
      return token;
    },
    /** The newest unused magic-link token mailed to `email`. */
    lastMagicLink(email: string): string | null {
      const normalized = email.trim().toLowerCase();
      return [...magic.entries()].reverse().find(([, v]) => v.email === normalized)?.[0] ?? null;
    },
    /**
     * Signs in as the link's email (creating the account), returns where the link lands.
     * HARDEN-mfa: a link mailed before its account enrolled lands on the error callback with
     * `error=TWO_FACTOR_REQUIRED`, signed out (the web's errorCallbackURL, api/client.ts).
     */
    openMagicLink(token: string): string | null {
      const link = magic.get(token);
      if (!link) return null;
      magic.delete(token);
      if (d.twoFactorEnabled(link.email))
        return `/login?link=invalid&redirect=${encodeURIComponent(link.callbackPath)}&error=TWO_FACTOR_REQUIRED`;
      let account = d.accounts.get(link.email);
      if (!account) {
        account = {
          email: link.email,
          password: `!no-password-${Math.random()}`,
          label: link.email,
          me: {
            user: { id: uid(0x0e10, ++seq), email: link.email, name: link.name ?? "" },
            memberships: [],
            agency: null,
            entitlements: {},
            mfa: { enrolled: false, operator_required: false, required_company_ids: [] },
          },
        };
        d.accounts.set(link.email, account);
        passwordless.add(link.email);
      }
      d.setCurrent(link.email);
      startSession(link.email);
      return link.callbackPath;
    },
    hasPassword(email: string) {
      return !passwordless.has(email);
    },
    changePassword(email: string, current: string, next: string): string | null {
      const account = d.accounts.get(email);
      if (!account || passwordless.has(email)) return "CREDENTIAL_ACCOUNT_NOT_FOUND";
      if (account.password !== current) return "INVALID_PASSWORD";
      if (next.length < 8) return "PASSWORD_TOO_SHORT";
      account.password = next;
      return null;
    },
    /** "Forgot password": mails a reset link for a known address, nothing for an unknown one. */
    requestPasswordReset(email: string) {
      const normalized = email.trim().toLowerCase();
      if (!d.accounts.has(normalized)) return;
      const token = `rp_${(++seq).toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      resets.set(token, normalized);
      mail(normalized, "Reset your CompanyOS password", `/reset-password?token=${token}`);
    },
    /** The newest unused reset token mailed to `email`. */
    lastResetToken(email: string): string | null {
      const normalized = email.trim().toLowerCase();
      return [...resets.entries()].reverse().find(([, v]) => v === normalized)?.[0] ?? null;
    },
    /** Null when reset (the token is spent, every session of the account ends), else the code. */
    resetPassword(token: string, next: string): string | null {
      const email = resets.get(token);
      const account = email ? d.accounts.get(email) : undefined;
      if (!email || !account) return "INVALID_TOKEN";
      if (next.length < 8) return "PASSWORD_TOO_SHORT";
      resets.delete(token);
      account.password = next;
      passwordless.delete(email);
      for (let i = sessions.length - 1; i >= 0; i--)
        if (sessions[i]?.email === email) sessions.splice(i, 1);
      if (d.current() === email) {
        currentSession = null;
        d.setCurrent(null);
      }
      return null;
    },
    /** A link-only account's first password; null when set, else the code. */
    setPassword(email: string, next: string): string | null {
      const account = d.accounts.get(email);
      if (!account) return "CREDENTIAL_ACCOUNT_NOT_FOUND";
      if (!passwordless.has(email)) return "PASSWORD_ALREADY_SET";
      if (next.length < 8) return "PASSWORD_TOO_SHORT";
      account.password = next;
      passwordless.delete(email);
      return null;
    },
    updateName(email: string, name: string) {
      const account = d.accounts.get(email);
      if (account) account.me.user.name = name.trim();
    },
    peopleOf,
    /** The company's roles (the structure fake checks a seat's `role_id` against them). */
    rolesOf,
  };
}

export type AccessFake = ReturnType<typeof createAccessFake>;
